import React from 'react';
import { AppLayoutContainer } from '@zg-rentals/zrm-utils-js';
import { PageFooter } from '@zillow/page-frame';
import type { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';

import styled from 'styled-components';

import { ToastProvider, token } from '@zillow/constellation';
import { useNodeEnv, useRentalManagerUser, useRmwPaths, useRouter, useStaticApp } from '../hooks';
import cacheStorageUtils from '../utils/cacheUtils';
import { Link as v4Link } from 'react-router-dom';
import { browserUtils } from '../utils/browserUtils';
import { RequestWidget } from './RequestWidget';

import { type User, ZRMUtils } from '@zg-rentals/rental-manager-header-navigation';
import { onPageFrameLinkClick } from '@zg-rentals/datagrail';

export const StyledContainer = styled.div`
  background-color: ${token('colors.gray100')};
  width: 100%;
  position: relative;
  height: 100%;
`;

export const StyledFooterContainer = styled.div`
  background-color: ${token('colors.white')};
`;

export type AppContainerProps = {
  children: JSX.Element | Array<JSX.Element>;
  customerHeader?: JSX.Element;
  escapeAllLinks?: boolean | undefined;
  hideFooter?: boolean | undefined | null;
  hideHeader?: boolean | undefined;
  isLoading?: boolean | undefined;
  userModel?: User | undefined;
  isInMobileWebView?: boolean;
};

declare global {
  interface Window {
    rentalManagerApiWebSocket: WebSocketWrapper;
  }
}

export const AppContainer = ({
  children,
  customerHeader,
  escapeAllLinks = true,
  hideFooter = false,
  hideHeader = false,
  isLoading = false,
  userModel = undefined,
  isInMobileWebView = false,
}: AppContainerProps) => {
  const { user: userFromHook, logOut } = useRentalManagerUser();
  const { initialAuthComplete } = useStaticApp();

  const user = userModel || userFromHook;

  const { features } = user;
  const paths = useRmwPaths();
  const environment = useNodeEnv();
  const rentalManagerApiWebSocket = typeof window !== 'undefined' ? window.rentalManagerApiWebSocket : undefined;
  const router = useRouter();
  const pathname = router.location.pathname;
  const hasLoggedPageView = React.useRef(false);
  const includeSupportWidget = user.isMultifamilyCustomer || user.isSingleFamilyEnterpriseCustomer;

  // provide proxy path to headerNav for making notification center requests
  const proxyPath = paths.proxy;

  const logoPathOverride = React.useMemo(
    () => ZRMUtils.queryUtils.getPathWithPresistedQueryParams(`${paths.zrmHomePage}/${router.location.search}`),
    [router.location.search],
  );

  const handleLogoutClick = () => {
    logOut();
    cacheStorageUtils.clear();
  };

  // GA Tracking
  React.useEffect(() => {
    if (!initialAuthComplete || hasLoggedPageView.current) {
      return;
    }
    hasLoggedPageView.current = true;
  }, [initialAuthComplete, user]);

  React.useEffect(() => {
    // only track subsequent page changes
    if (!initialAuthComplete || !hasLoggedPageView.current) {
      return;
    }
  }, [router.location.pathname, initialAuthComplete]);

  let zgApi;
  if (typeof window !== 'undefined') {
    if (browserUtils.isLocalhost(window.location.host)) {
      // local test environment
      zgApi = environment.zgApi;
    } else {
      // all othe environments skip proxy
      zgApi = window.location.origin;
    }
  } else {
    // Server
    zgApi = environment.zgApi;
  }

  return (
    <StyledContainer>
      <ToastProvider>
        {customerHeader}
        <AppLayoutContainer
          isLoading={isLoading}
          hideHeaderNavigation={!!customerHeader || hideHeader}
          altLogo={!user.isEnterpriseCustomer}
          brand="zillow"
          displayAnalytics={features?.canAccessMainAnalytics}
          escapeAllLinks={escapeAllLinks}
          handleLogoutClick={handleLogoutClick}
          logoPathOverride={logoPathOverride}
          pathname={pathname}
          proxyPath={proxyPath}
          reactRouterLink={v4Link}
          useRelativePaths={true}
          userModel={user}
          webSocketInstance={rentalManagerApiWebSocket}
          zgApi={zgApi}
          isInMobileWebView={isInMobileWebView}
        >
          {children}
          {includeSupportWidget && <RequestWidget />}

          {!hideFooter && (
            <StyledFooterContainer>
              <PageFooter onLinkClick={onPageFrameLinkClick} />
            </StyledFooterContainer>
          )}
        </AppLayoutContainer>
      </ToastProvider>
    </StyledContainer>
  );
};
